import { ActivitiesDetail, ActivitiesOverview } from '@/modules/activities'
import { ActivitiesFileArchive } from '@/modules/activities/views/ActivitiesFileArchive'
import { ActivitiesStatus } from '@/modules/activities/views/ActivitiesStatus'
import { RemainingDrivingTime } from '@/modules/activities/views/RemainingDrivingTime'
import { Checklists } from '@/modules/checklists/views/Checklists'
import { TodaysMandatoryChecklists } from '@/modules/checklists/views/TodaysMandatoryChecklists'
import { ViewChecklistResponse } from '@/modules/checklists/views/ViewChecklistResponse'
import {
  AbsenceSettings,
  ActivitiesSettings,
  ChecklistTemplatesSettings,
  CompanySettings,
  CompanyRulesSettings,
  CreateUserTypeSettings,
  DropdownsSettings,
  EditSalaryWorkTime,
  GeneralSettings,
  IntegrationsSettings,
  IssueSettings,
  PermissionSettings,
  SalarySettings,
  TerminalSettings,
  TimeEntrySettings,
  UserDocumentSettings,
} from '@/modules/companies'
import { CreateSalaryWorkTime } from '@/modules/companies/CompanySettings/components/SalarySettings/CreateSalaryWorkTime'
import { CompanyRuleFormView } from '@/modules/companies/CompanySettings/components/CompanyRuleSettings'
import { ExportArbeidstilsynet } from '@/modules/companies/CompanySettings/view/export-arbeidstilsynet'
import { ExportsSettings } from '@/modules/companies/CompanySettings/view/ExportsSettings'
import { FATSParametersSettings } from '@/modules/companies/CompanySettings/view/FATSParametersSettings'
import { HandbookSettings } from '@/modules/companies/CompanySettings/view/HandbookSettings'
import { InfringementsSettings } from '@/modules/companies/CompanySettings/view/InfringementsSettings'
import { RoutePlannerSettings } from '@/modules/companies/CompanySettings/view/RoutePlannerSettings'
import { SalaryExport } from '@/modules/companies/CompanySettings/view/SalaryExport'
import { UserEquipmentSettings } from '@/modules/companies/CompanySettings/view/UserEquipmentSettings'
import { VehicleFleetSettings } from '@/modules/companies/CompanySettings/view/VehicleFleetSettings'
import { Dashboard } from '@/modules/dashboard'
import { Learning } from '@/modules/learning'
import { HtmlAsPdf } from '@/modules/exports/view/HtmlAsPdf'
import { UserExports } from '@/modules/exports/view/UserExports'
import {
  CreateForm,
  EditForm,
  FormCreator,
  ResponseOverview,
  SubmitForm,
} from '@/modules/formcreator'
import { ResponseSums } from '@/modules/formcreator/views/ResponseSums'
import { CreateThread, Forum, Thread } from '@/modules/forum'
import {
  Handbook,
  HandbookCreateEntry,
  HandbookEntry,
} from '@/modules/handbook'
import {
  Infringement,
  InfringementAnalytics,
  Infringements,
} from '@/modules/infringements'
import {
  WorkDayInfringement,
  WorkDayInfringements,
} from '@/modules/work-infringements'
import { UserInfringements } from '@/modules/infringements/views/UserInfringements'
import { CreateIssue, Issue, Issues, IssuesTvDisplay } from '@/modules/issues'
import { RoutePlan, RoutePlanner } from '@/modules/routePlanner'
import { CreateRoutePlan } from '@/modules/routePlanner/view'
import { SalaryBase } from '@/modules/salary/SalaryBase'
import { SalaryBases } from '@/modules/salary/SalaryBases'
import { WorkPeriodDateCellPreview } from '@/modules/salary'
import { WorkPeriodApproveView } from '@/modules/salary/WorkPeriodApproveView'
import { WorkPeriodOverview } from '@/modules/salary/WorkPeriodOverview'
import {
  AbsenceDetails,
  AbsenceLockEdit,
  Absences,
  AbsencesTvDisplay,
  CreateTimeEntry,
  TimeEntries,
} from '@/modules/timesheets'
import Users, { CreateUser, UserSettings } from '@/modules/users'
import { UserProfile } from '@/modules/users/UserProfile'
import { VehicleDocumentsView } from '@/modules/vehicles/documents'
import { VehicleExpirationsView } from '@/modules/vehicles/expirations'
import { VehicleServicePlansView } from '@/modules/vehicles/services'
import { FleetOverview, Vehicle } from '@/modules/vehicles/fleet'
import { PERMISSIONS } from '@/utils'
import { createBrowserRouter, Navigate, useParams } from 'react-router'
import MainLayout from './MainLayout'
import { AdminRequired, AuthRequired } from './permission-containers'
import { VehicleActivitiesFileArchive } from '@/modules/activities/views/VehicleActivitiesFileArchive'
import { TachoVehicleFileBrowser } from '@/modules/activities/views/TachoVehicleFileBrowser'
import { TachoDriverFileBrowser } from '@/modules/activities/views/TachoDriverFileBrowser'
import { VehicleActivitiesStatus } from '@/modules/activities/views/VehicleActivitiesStatus'
import { StartStopCountryRegistrations } from '@/modules/activities/views/StartStopCountryRegistrations'
import { DrivingWithoutCard } from '@/modules/activities/views/DrivingWithoutCard'
import { OutOfScopeDriving } from '@/modules/activities/views/out-of-scope-driving'

const RedirectWithFileId = ({ to }: { to: string }) => {
  const { fileId } = useParams()
  return <Navigate to={`${to}/${fileId}`} replace />
}

export const createAuthRouter = (companyShortName: string, _userId: string) =>
  createBrowserRouter(
    [
      {
        path: '/export',
        element: (
          <AuthRequired>
            <HtmlAsPdf />
          </AuthRequired>
        ),
      },
      {
        path: '/*',
        element: <MainLayout />,
        children: [
          {
            path: 'dashboard',
            element: (
              <AuthRequired>
                <Dashboard />
              </AuthRequired>
            ),
          },
          {
            path: 'learning',
            element: (
              <AuthRequired module="learning">
                <Learning />
              </AuthRequired>
            ),
          },
          {
            path: 'mandatory-checklists',
            element: (
              <AuthRequired>
                <TodaysMandatoryChecklists />
              </AuthRequired>
            ),
          },
          // Users
          {
            path: 'users/create',
            element: (
              <AuthRequired permissions={PERMISSIONS.users.add.user}>
                <CreateUser />
              </AuthRequired>
            ),
          },
          {
            path: 'users/:userId',
            element: (
              <AuthRequired permissions={PERMISSIONS.users.view.user}>
                <UserProfile />
              </AuthRequired>
            ),
          },
          {
            path: 'users',
            element: (
              <AuthRequired permissions={PERMISSIONS.users.view.user}>
                <Users />
              </AuthRequired>
            ),
          },
          {
            path: 'users/:userId/salary/create',
            element: (
              <AuthRequired permissions={PERMISSIONS.users.add.user}>
                <CreateSalaryWorkTime />
              </AuthRequired>
            ),
          },
          {
            path: 'users/:userId/salary/:salaryWorkTimeId',
            element: (
              <AuthRequired permissions={PERMISSIONS.users.add.user}>
                <EditSalaryWorkTime />
              </AuthRequired>
            ),
          },
          {
            path: 'users/:userId',
            children: [
              {
                path: 'salary',
                element: <Navigate to=".." replace />,
              },
            ],
          },
          // Activities
          {
            path: 'activities/:userId/:date',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.view.driveractivity}
              >
                <ActivitiesDetail />
              </AuthRequired>
            ),
          },
          {
            path: 'remaining-driving',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.view.driveractivity}
              >
                <RemainingDrivingTime />
              </AuthRequired>
            ),
          },
          {
            path: 'work-periods',
            element: (
              <AuthRequired module="salary">
                <WorkPeriodOverview />
              </AuthRequired>
            ),
          },
          {
            path: 'work-periods/:userId',
            element: (
              <AuthRequired module="salary">
                <WorkPeriodOverview />
              </AuthRequired>
            ),
          },

          {
            path: 'approve-work-periods',
            element: (
              <AuthRequired module="salary">
                <WorkPeriodApproveView />
              </AuthRequired>
            ),
          },
          {
            path: 'approve-work-periods/:userId',
            element: (
              <AuthRequired module="salary">
                <WorkPeriodApproveView />
              </AuthRequired>
            ),
          },
          {
            path: 'preview/work-period-date-cells',
            element: <WorkPeriodDateCellPreview />,
          },
          {
            path: 'activities/:userId',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.view.driveractivity}
              >
                <ActivitiesOverview />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/status',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <ActivitiesStatus />
              </AuthRequired>
            ),
          },

          {
            path: 'analytics/vehicle-status',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <VehicleActivitiesStatus />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/file-archive',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <ActivitiesFileArchive />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/file-archive/:fileId',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <TachoDriverFileBrowser />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/vehicle-file-archive',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <VehicleActivitiesFileArchive />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/vehicle-file-archive/:fileId',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <TachoVehicleFileBrowser />
              </AuthRequired>
            ),
          },

          {
            path: 'analytics/start-stop-country-registrations',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <StartStopCountryRegistrations />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/driving-without-card',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.view.driveractivity}
              >
                <DrivingWithoutCard />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/out-of-scope-driving',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.change.driveractivity}
              >
                <OutOfScopeDriving />
              </AuthRequired>
            ),
          },
          {
            path: 'activities',
            element: (
              <AuthRequired
                module="activities"
                permissions={PERMISSIONS.activities.view.driveractivity}
              >
                <ActivitiesOverview />
              </AuthRequired>
            ),
          },
          // Forum
          {
            path: 'forum',
            element: (
              <AuthRequired permissions={PERMISSIONS.forum.view.thread}>
                <Forum />
              </AuthRequired>
            ),
          },
          {
            path: 'forum/create',
            element: (
              <AuthRequired permissions={PERMISSIONS.forum.add.thread}>
                <CreateThread />
              </AuthRequired>
            ),
          },

          {
            path: 'forum/create-direct',
            element: (
              <AuthRequired permissions={PERMISSIONS.forum.add.thread}>
                <CreateThread isDirectMessage />
              </AuthRequired>
            ),
          },
          {
            path: 'forum/:threadId',
            element: (
              <AuthRequired permissions={PERMISSIONS.forum.view.thread}>
                <Thread />
              </AuthRequired>
            ),
          },
          // Handbook
          {
            path: 'handbook/create/:folderId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.handbook.add.manualentry}
                module="handbook"
              >
                <HandbookCreateEntry />
              </AuthRequired>
            ),
          },
          {
            path: 'handbook/:folderSlug/:entrySlug',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.handbook.view.manualentry}
                module="handbook"
              >
                <HandbookEntry />
              </AuthRequired>
            ),
          },
          {
            path: 'handbook/:folderSlug',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.handbook.view.manualfolder}
                module="handbook"
              >
                <Handbook />
              </AuthRequired>
            ),
          },
          {
            path: 'handbook',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.handbook.view.manualfolder}
                module="handbook"
              >
                <Handbook />
              </AuthRequired>
            ),
          },
          // FormCreator
          {
            path: 'formcreator',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.view.formtemplate}
                module="handbook"
              >
                <FormCreator />
              </AuthRequired>
            ),
          },
          {
            path: 'formcreator/create',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.add.formtemplate}
                module="handbook"
              >
                <CreateForm />
              </AuthRequired>
            ),
          },
          {
            path: 'formcreator/edit/:formId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.change.formtemplate}
                module="handbook"
              >
                <EditForm />
              </AuthRequired>
            ),
          },
          {
            path: 'formcreator/edit',
            element: <Navigate to="/formcreator" replace />,
          },
          {
            path: 'formcreator/:formId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.view.formtemplate}
                module="handbook"
              >
                <SubmitForm />
              </AuthRequired>
            ),
          },
          {
            path: 'formcreator/:formId/responses',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.view.filledoutform}
                module="handbook"
              >
                <ResponseOverview />
              </AuthRequired>
            ),
          },
          {
            path: 'formcreator/:formId/sums',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.change.filledoutform}
                module="handbook"
              >
                <ResponseSums />
              </AuthRequired>
            ),
          },
          // Checklists
          {
            path: 'checklists',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.change.filledoutform}
                module="handbook"
              >
                <Checklists />
              </AuthRequired>
            ),
          },
          {
            path: 'checklists/:checklistId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.formcreator.change.filledoutform}
                module="handbook"
              >
                <ViewChecklistResponse />
              </AuthRequired>
            ),
          },
          // Issues
          {
            path: 'issues',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.issues.view.issue}
                module="issues"
              >
                <Issues />
              </AuthRequired>
            ),
          },
          {
            path: 'issues/create',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.issues.add.issue}
                module="issues"
              >
                <CreateIssue />
              </AuthRequired>
            ),
          },
          {
            path: 'issues/tv-display',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.issues.view.issue}
                module="issues"
              >
                <IssuesTvDisplay />
              </AuthRequired>
            ),
          },
          {
            path: 'issues/:issueId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.issues.view.issue}
                module="issues"
              >
                <Issue />
              </AuthRequired>
            ),
          },
          // Timesheets
          {
            path: 'time-entries',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.timesheets.view.timeentry}
                module="timesheets"
              >
                <TimeEntries />
              </AuthRequired>
            ),
          },
          {
            path: 'time-entries/:userId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.timesheets.view.timeentry}
                module="timesheets"
              >
                <TimeEntries />
              </AuthRequired>
            ),
          },
          {
            path: 'time-entries/register',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.timesheets.add.timeentry}
                module="timesheets"
              >
                <CreateTimeEntry />
              </AuthRequired>
            ),
          },
          {
            path: 'absences',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.timesheets.view.absence,
                  PERMISSIONS.users.view.user,
                ]}
                module="absences"
              >
                <Absences />
              </AuthRequired>
            ),
          },
          {
            path: 'absences/tv-display',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.timesheets.view.absence,
                  PERMISSIONS.users.view.user,
                ]}
                module="absences"
              >
                <AbsencesTvDisplay />
              </AuthRequired>
            ),
          },
          {
            path: 'absences/edit-lock/:id?',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.timesheets.change.absencelockconfig}
                module="absences"
              >
                <AbsenceLockEdit />
              </AuthRequired>
            ),
          },
          {
            path: 'absences/:absenceId',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.timesheets.view.absence,
                  PERMISSIONS.users.view.user,
                ]}
                module="absences"
              >
                <AbsenceDetails />
              </AuthRequired>
            ),
          },
          // Profile Settings
          {
            path: 'usersettings',
            element: (
              <AuthRequired>
                <UserSettings />
              </AuthRequired>
            ),
          },
          // Company Settings
          {
            path: 'settings',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.companies.view.companysetting}
              >
                <CompanySettings />
              </AuthRequired>
            ),
            children: [
              {
                index: true,
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                  >
                    <GeneralSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'activities',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.activities.change.driveractivity}
                    module="activities"
                  >
                    <ActivitiesSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'issues',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.issues.add.issuecategory}
                    module="issues"
                  >
                    <IssueSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'time-entries',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.companies.change.companysetting}
                    module="timesheets"
                  >
                    <TimeEntrySettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'absence',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.timesheets.add.absencetype}
                    module="absences"
                  >
                    <AbsenceSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'infringements',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.infringements.change.infringement}
                    module="infringements"
                  >
                    <InfringementsSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'terminal-network',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.terminal.change.terminal}
                    module="terminal"
                  >
                    <TerminalSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'user-types/create',
                element: (
                  <AuthRequired permissions={PERMISSIONS.users.add.usertype}>
                    <CreateUserTypeSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'user-types',
                element: (
                  <AuthRequired
                    permissions={[
                      PERMISSIONS.users.add.usertype,
                      PERMISSIONS.users.change.usertype,
                    ]}
                  >
                    <PermissionSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'export',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.companies.export.exportReports}
                    module="main"
                  >
                    <ExportsSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'salary-export',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.companies.export.exportReports}
                    module="main"
                  >
                    <SalaryExport />
                  </AuthRequired>
                ),
              },
              {
                path: 'export-arbeidstilsynet',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.companies.export.exportReports}
                    module="main"
                  >
                    <ExportArbeidstilsynet />
                  </AuthRequired>
                ),
              },
              {
                path: 'fats-parameters',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                    module="workInfringements"
                  >
                    <FATSParametersSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'salary',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                  >
                    <SalarySettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'company-rules',
                element: (
                  <AdminRequired>
                    <CompanyRulesSettings />
                  </AdminRequired>
                ),
              },
              {
                path: 'company-rules/create',
                element: (
                  <AdminRequired>
                    <CompanyRuleFormView />
                  </AdminRequired>
                ),
              },
              {
                path: 'company-rules/:ruleId',
                element: (
                  <AdminRequired>
                    <CompanyRuleFormView />
                  </AdminRequired>
                ),
              },
              {
                path: 'salary/create',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                  >
                    <CreateSalaryWorkTime />
                  </AuthRequired>
                ),
              },
              {
                path: 'salary/:salaryWorkTimeId',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                  >
                    <EditSalaryWorkTime />
                  </AuthRequired>
                ),
              },
              {
                path: 'integrations',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                    module="activities"
                  >
                    <IntegrationsSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'handbook',
                element: (
                  <AuthRequired
                    permissions={PERMISSIONS.handbook.change.manualentry}
                    module="handbook"
                  >
                    <HandbookSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'dropdowns',
                element: (
                  <AuthRequired
                    permissions={[
                      PERMISSIONS.users.add.usertype,
                      PERMISSIONS.users.change.usertype,
                    ]}
                  >
                    <DropdownsSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'user-documents',
                element: (
                  <AuthRequired
                    permissions={[
                      PERMISSIONS.users.add.userdocument,
                      PERMISSIONS.users.change.userdocument,
                    ]}
                  >
                    <UserDocumentSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'user-equipment',
                element: (
                  <AuthRequired
                    permissions={[
                      PERMISSIONS.users.add.userequipment,
                      PERMISSIONS.users.change.userequipment,
                    ]}
                  >
                    <UserEquipmentSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'checklist-templates',
                element: (
                  <AuthRequired
                    permissions={[
                      PERMISSIONS.checklists.add.checklisttemplate,
                      PERMISSIONS.checklists.change.checklisttemplate,
                    ]}
                  >
                    <ChecklistTemplatesSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'route-planner',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                    module="routePlanner"
                  >
                    <RoutePlannerSettings />
                  </AuthRequired>
                ),
              },
              {
                path: 'vehicles',
                element: (
                  <AuthRequired
                    permissions={
                      PERMISSIONS.companies.change.company_general_settings
                    }
                    module="vehicles"
                  >
                    <VehicleFleetSettings />
                  </AuthRequired>
                ),
              },
            ],
          },
          // User Export
          {
            path: 'user-exports',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.companies.view.companysetting}
              >
                <UserExports />
              </AuthRequired>
            ),
          },
          // Vehicle Fleet
          {
            path: 'vehicle-fleet',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.vehicles.view.vehicle}
                module="vehicles"
              >
                <FleetOverview />
              </AuthRequired>
            ),
          },
          {
            path: 'vehicle-documents',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.vehicles.view.vehicle,
                  PERMISSIONS.vehicles.view.vehicledocument,
                ]}
                module="vehicles"
              >
                <VehicleDocumentsView />
              </AuthRequired>
            ),
          },
          {
            path: 'vehicle-expirations',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.vehicles.view.vehicle,
                  PERMISSIONS.vehicles.view.vehicleexpiration,
                ]}
                module="vehicles"
              >
                <VehicleExpirationsView />
              </AuthRequired>
            ),
          },
          {
            path: 'vehicle-services',
            element: (
              <AuthRequired
                permissions={[
                  PERMISSIONS.vehicles.view.vehicle,
                  PERMISSIONS.vehicles.view.vehicleexpiration,
                ]}
                module="vehicles"
              >
                <VehicleServicePlansView />
              </AuthRequired>
            ),
          },
          {
            path: 'vehicle-fleet/:vehicleId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.vehicles.view.vehicle}
                module="vehicles"
              >
                <Vehicle />
              </AuthRequired>
            ),
          },
          // Salary
          {
            path: 'salary-bases',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.salary.change.salarybase}
                module="salary"
              >
                <SalaryBases />
              </AuthRequired>
            ),
          },
          {
            path: 'salary-bases/:salaryBaseId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.salary.change.salarybaseentry}
                module="salary"
              >
                <SalaryBase />
              </AuthRequired>
            ),
          },
          // Infringements
          {
            path: 'infringements',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="infringements"
              >
                <Infringements />
              </AuthRequired>
            ),
          },
          {
            path: 'analytics/infringements',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="infringements"
              >
                <AdminRequired renderMessage="show">
                  <InfringementAnalytics />
                </AdminRequired>
              </AuthRequired>
            ),
          },
          {
            path: 'infringements/:infringementId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="infringements"
              >
                <Infringement />
              </AuthRequired>
            ),
          },
          {
            path: 'infringements/user/:userId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="infringements"
              >
                <UserInfringements />
              </AuthRequired>
            ),
          },
          {
            path: 'work-day-infringements',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="workInfringements"
              >
                <AdminRequired>
                  <WorkDayInfringements />
                </AdminRequired>
              </AuthRequired>
            ),
          },
          {
            path: 'work-day-infringements/:infringementId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.infringements.view.infringement}
                module="workInfringements"
              >
                <AdminRequired>
                  <WorkDayInfringement />
                </AdminRequired>
              </AuthRequired>
            ),
          },
          // Route Planner
          {
            path: 'route-planner',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.routePlanner.view.routePlan}
                module="routePlanner"
              >
                <RoutePlanner />
              </AuthRequired>
            ),
          },
          {
            path: 'route-planner/create',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.routePlanner.add.routePlan}
                module="routePlanner"
              >
                <CreateRoutePlan />
              </AuthRequired>
            ),
          },
          {
            path: 'route-planner/:routePlanId',
            element: (
              <AuthRequired
                permissions={PERMISSIONS.routePlanner.view.routePlan}
                module="routePlanner"
              >
                <RoutePlan />
              </AuthRequired>
            ),
          },
          // Other Redirects
          {
            path: 'activities/status',
            element: <Navigate to="/analytics/status" replace />,
          },
          {
            path: 'vehicles/status',
            element: <Navigate to="/analytics/vehicle-status" replace />,
          },
          {
            path: 'activities/file-archive',
            element: <Navigate to="/analytics/file-archive" replace />,
          },
          {
            path: 'activities/file-archive/:fileId',
            element: <RedirectWithFileId to="/analytics/file-archive" />,
          },
          {
            path: 'activities/vehicle-file-archive',
            element: <Navigate to="/analytics/vehicle-file-archive" replace />,
          },
          {
            path: 'activities/vehicle-file-archive/:fileId',
            element: (
              <RedirectWithFileId to="/analytics/vehicle-file-archive" />
            ),
          },
          {
            path: 'activities/start-stop-country-registrations',
            element: (
              <Navigate
                to="/analytics/start-stop-country-registrations"
                replace
              />
            ),
          },
          {
            path: 'vehicles/driving-without-card',
            element: <Navigate to="/analytics/driving-without-card" replace />,
          },
          {
            path: 'activities/out-of-scope-driving',
            element: <Navigate to="/analytics/out-of-scope-driving" replace />,
          },
          {
            path: 'infringements/analytics',
            element: <Navigate to="/analytics/infringements" replace />,
          },
          {
            path: 'absences/:absenceId',
            element: <Navigate to="/absences" replace />,
          },
          {
            path: 'time-entries/:timeEntryId',
            element: <Navigate to="/time-entries" replace />,
          },
          {
            path: '',
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: '*',
            element: <Navigate to="/dashboard" replace />,
          },
        ],
      },
    ],
    {
      basename: `/c/${companyShortName}`,
    }
  )
